import React, { useEffect, useState, useMemo, type ChangeEvent } from "react";
import {
  Constants,
  getUkcAgilityActSeconds,
  getUkcAgilityDivisionFromHeight,
  getUkcAgilitySctSeconds,
  getUkcAgilityStandardScore,
  getUkcAgilityTimeFaults,
  isUkcAgilityDynamicClass,
  isUkcAgilityThreeOfAKindClass,
} from "@eagerdog/constants";
import { IShow, IScore, AgilityScoreDto } from "@eagerdog/interfaces";

import Dropdown, { IOption } from "src/components/Dropdown/Dropdown";
import Input from "src/components/Input/Input";
import StopWatch, { convertTimeToMinutes, convertTimeToSeconds } from "../StopWatch/StopWatch";

import "./AgilityResults.scss";
import Checkbox from "../../Checkbox/Checkbox";

interface IProps {
  show?: IShow,
  result: IScore,
  onChange(result: AgilityScoreDto): void
}

const AgilityResults: React.FC<IProps> = ({ show, result, onChange}) => {
  const [time, setTime] = useState<string>("");
  const [points, setPoints] = useState<number>(result.agility?.points ?? result.agility?.score ?? 0);
  const [faults, setFaults] = useState<number>(result.agility?.faults ?? 0);
  const [manualTimeFaults, setManualTimeFaults] = useState<string>(
    result.agility?.time_faults != null ? String(result.agility.time_faults) : "0"
  );
  const [timeFaultsOverride, setTimeFaultsOverride] = useState<boolean>(false);

  const [excluded, setExcluded] = useState<boolean>(false);

  const [resultType, setResultType] = useState<IOption>({ id: Constants.score_result_type.qualified, value: "Qualified" });
  const [loaded, setLoaded] = useState<boolean>(false);

  const isDynamicClass = useMemo(() => {
    return result.dog_class?.level && isUkcAgilityDynamicClass(result.dog_class.level);
  }, [result.dog_class?.level]);

  const isThreeOfAKindClass = useMemo(() => {
    return !!result.dog_class?.level && isUkcAgilityThreeOfAKindClass(result.dog_class.level);
  }, [result.dog_class?.level]);

  const resultOptions:IOption[] = useMemo(() => [
    { id: Constants.score_result_type.qualified, value: "Qualified" },
    { id: Constants.score_result_type.not_qualified, value: "NQ"},
    { id: Constants.score_result_type.absent, value: "Absent" },
    { id: Constants.score_result_type.excused, value: "Excused" }
  ], []);

  const isQualified = resultType.id === Constants.score_result_type.qualified;
  const showRunDetails =
    resultType.id === Constants.score_result_type.qualified ||
    resultType.id === Constants.score_result_type.not_qualified;

  const formatClock = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const { displaySCT, displayACT, displayDivision, actSeconds } = useMemo(() => {
    const sctInSeconds = getUkcAgilitySctSeconds(show, result.dog_class?.level);
    const division = getUkcAgilityDivisionFromHeight(result.height_at_withers);
    const assignedCourseTime = getUkcAgilityActSeconds(sctInSeconds, division);

    if (sctInSeconds <= 0) {
      return { displaySCT: "", displayACT: "", displayDivision: division, actSeconds: 0 };
    }

    const minSuffix = result.jump_height_type === Constants.jump_height_type.minimum ? " (M)" : "";

    return {
      displaySCT: formatClock(sctInSeconds),
      displayACT: formatClock(assignedCourseTime),
      displayDivision: division ? `${division}${minSuffix}` : "",
      actSeconds: assignedCourseTime,
    };
  }, [show, result.dog_class?.level, result.height_at_withers, result.jump_height_type]);

  const autoTimeFaults = useMemo(() => {
    if (isDynamicClass || isThreeOfAKindClass) {
      return 0;
    }
    return getUkcAgilityTimeFaults(convertTimeToSeconds(time), actSeconds);
  }, [isDynamicClass, isThreeOfAKindClass, time, actSeconds]);

  const timeFaults = useMemo(() => {
    if (isDynamicClass || isThreeOfAKindClass) {
      return 0;
    }
    if (actSeconds > 0 && !timeFaultsOverride) {
      return autoTimeFaults;
    }
    const parsed = Number(manualTimeFaults);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [isDynamicClass, isThreeOfAKindClass, actSeconds, timeFaultsOverride, autoTimeFaults, manualTimeFaults]);

  const computedScore = useMemo(() => {
    return getUkcAgilityStandardScore(faults, timeFaults);
  }, [faults, timeFaults]);

  const [hasChanged, setHasChanged] = useState<boolean>(false);

  useEffect(() => {
    if (!loaded) {
      if (result?.agility) {
        const { result_type } = result.agility;
        for (let ro in resultOptions) {
          if (resultOptions[ro].id === result_type) {
            setResultType(resultOptions[ro]);
            break;
          }
        }

        if (result.agility.time) {
          const timeInSeconds = Number(result.agility.time);
          setTime(convertTimeToMinutes(timeInSeconds));
        }

        setExcluded(!!result.agility.excluded_from_placement);
        if (result.agility.time_faults != null) {
          setManualTimeFaults(String(result.agility.time_faults));
        }
      }
      setLoaded(true);
    }
  }, [loaded, result, resultOptions]);

  useEffect(() => {
    if (hasChanged) {
      onChange({
        score: isDynamicClass || !isQualified ? 0 : computedScore,
        points: isThreeOfAKindClass ? points : undefined,
        faults: isThreeOfAKindClass ? undefined : (faults || 0),
        time_faults: isDynamicClass || isThreeOfAKindClass ? undefined : timeFaults,
        time: convertTimeToSeconds(time),
        result_type: resultType.id,
        excluded_from_placement: excluded,
      });

      setHasChanged(false);
    }
  }, [hasChanged, computedScore, points, faults, timeFaults, time, resultType, onChange, excluded, isDynamicClass, isThreeOfAKindClass, isQualified]);

  const handleTimeChange = (value: string) => {
    setTime(value);
    setTimeFaultsOverride(false);
    setHasChanged(true);
  }

  const handleFaultsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFaults(Number(event.target.value));
    setHasChanged(true);
  }

  const handleTimeFaultsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setManualTimeFaults(event.target.value);
    setTimeFaultsOverride(true);
    setHasChanged(true);
  }

  const handlePointsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPoints(Number(event.target.value));
    setHasChanged(true);
  }

  return (
    <div className="AgilityResults">
      {(displaySCT || displayACT || displayDivision) && (
        <div className="act-display">
          {displayDivision && (
            <div className="division-info">{displayDivision}</div>
          )}
          {displaySCT && (
            <div className="time-info">
              <span className="time-label"><strong>SCT</strong> (Standard Course Time):</span>
              <span className="time-value">{displaySCT}</span>
            </div>
          )}
          {displayACT && (
            <div className="time-info">
              <span className="time-label"><strong>ACT</strong> (Assigned Course Time):</span>
              <span className="time-value">{displayACT}</span>
            </div>
          )}
        </div>
      )}
      <Dropdown value={resultType} label="Result" onChange={(_e, value: IOption) => { setResultType(value); setHasChanged(true); }} options={resultOptions} placeholder="" />
      {showRunDetails &&
        <>
          <StopWatch label="Time" time={time} onChange={handleTimeChange} />
          {isThreeOfAKindClass && (
            <Input type="number" onChange={handlePointsChange} label="Points" placeholder="Enter points (e.g., 26)" value={points.toString()} />
          )}
          {!isDynamicClass && (
            <>
              <Input type="number" inputMode="decimal" onChange={handleFaultsChange} label="Faults" placeholder="Enter course faults (e.g., 5)" value={faults.toString()} />
              <Input
                type="text"
                inputMode="decimal"
                onChange={handleTimeFaultsChange}
                label="Time Faults"
                placeholder={actSeconds > 0 ? "Auto from time over ACT" : "Enter time faults"}
                value={actSeconds > 0 && !timeFaultsOverride ? timeFaults.toString() : manualTimeFaults}
              />
              <Input
                type="text"
                disabled
                readOnly
                label="Score"
                value={isQualified ? computedScore.toString() : ""}
              />
              <div className="info-text" style={{ padding: '10px', marginBottom: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
                {isQualified
                  ? "Score starts at 200, then course faults and time faults (seconds over ACT) are deducted."
                  : "NQ does not receive a score. Time and faults are still saved."}
                {actSeconds <= 0 ? " Set SCT in Additional Settings to auto-calculate time faults, or type them here." : ""}
              </div>
            </>
          )}
          {isDynamicClass && !isThreeOfAKindClass && (
            <div className="info-text" style={{ padding: '10px', marginBottom: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
              <strong>Score:</strong> N/A (Dynamic class)
            </div>
          )}
          {isDynamicClass && !isThreeOfAKindClass && (
            <Input type="number" onChange={handleFaultsChange} label="Faults" placeholder="Enter the number of faults (e.g., 0)" value={faults.toString()} />
          )}
        </>
      }
      <Checkbox
        onChange={() => {
          setExcluded(!excluded);
          setHasChanged(true);
        }}
        value={excluded}
        id="excluded"
        label="Exclude this result from placements"
      />
    </div>
  );
};

export default AgilityResults;
